/**
 * BodyProjection3D — Projeção corporal 3D estimada do atleta
 * 
 * Renderiza um avatar humanóide paramétrico baseado nas medidas antropométricas.
 * NÃO substitui exame clínico — representação visual estimada.
 * 
 * Props:
 *   measurements: { height_cm, weight_kg, wingspan_cm, body_fat_pct, lean_mass_kg, muscle_mass_kg }
 *   gender: 'male' | 'female'
 *   athleteName: string
 */
import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, Text, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

// ─── Body Part Geometry Helpers ───────────────────────────────────────────────

const SKIN_MALE = '#e8b89a';
const SKIN_FEMALE = '#f0c5a8';
const MUSCLE_COLOR = '#c97a5a';

function lerp(a, b, t) {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}

// Calculate body proportions from measurements
function calculateProportions(measurements, gender) {
  const {
    height_cm = 170,
    weight_kg = 70,
    wingspan_cm,
    body_fat_pct = 15,
    lean_mass_kg,
    muscle_mass_kg,
  } = measurements || {};

  const h = height_cm / 170; // normalize to reference height
  const bmi = weight_kg / ((height_cm / 100) ** 2);
  const fatFactor = Math.max(0, Math.min(1, (body_fat_pct - 5) / 30)); // 5-35% range
  const muscleFactor = muscle_mass_kg
    ? Math.max(0, Math.min(1, (muscle_mass_kg / weight_kg - 0.3) / 0.2))
    : lean_mass_kg
      ? Math.max(0, Math.min(1, (lean_mass_kg / weight_kg - 0.6) / 0.2))
      : Math.max(0, Math.min(1, (1 - fatFactor) * 0.7));

  const wingspanRatio = wingspan_cm ? wingspan_cm / height_cm : 1.0;

  // Torso
  const torsoWidth = lerp(0.35, 0.50, (bmi - 18) / 12) * (gender === 'male' ? 1.05 : 0.95);
  const torsoDepth = lerp(0.20, 0.35, fatFactor);
  const torsoHeight = 0.55 * h;

  // Shoulders
  const shoulderWidth = torsoWidth * lerp(1.1, 1.35, muscleFactor) * (gender === 'male' ? 1.1 : 0.95);

  // Waist
  const waistWidth = torsoWidth * lerp(0.75, 1.0, fatFactor) * (gender === 'female' ? 1.05 : 1.0);

  // Hips
  const hipWidth = torsoWidth * (gender === 'female' ? lerp(1.0, 1.2, fatFactor) : lerp(0.85, 1.05, fatFactor));

  // Arms
  const armLength = 0.45 * h * wingspanRatio;
  const armThickness = lerp(0.06, 0.11, (muscleFactor + fatFactor) / 2);

  // Legs
  const legLength = 0.50 * h;
  const legThickness = lerp(0.08, 0.14, (muscleFactor + fatFactor) / 2);

  // Head
  const headSize = 0.12 * h;

  return {
    h, torsoWidth, torsoDepth, torsoHeight,
    shoulderWidth, waistWidth, hipWidth,
    armLength, armThickness,
    legLength, legThickness,
    headSize, muscleFactor, fatFactor, gender
  };
}

// ─── Body Parts ───────────────────────────────────────────────────────────────

const BodyPart = ({ position, args, color, radius = 0.02 }) => (
  <RoundedBox position={position} args={args} radius={radius} smoothness={4}>
    <meshStandardMaterial color={color} roughness={0.6} metalness={0.1} />
  </RoundedBox>
);

const Sphere = ({ position, radius, color }) => (
  <mesh position={position}>
    <sphereGeometry args={[radius, 16, 16]} />
    <meshStandardMaterial color={color} roughness={0.5} metalness={0.1} />
  </mesh>
);

// ─── Full Body Model ──────────────────────────────────────────────────────────

function HumanoidBody({ proportions }) {
  const groupRef = useRef();
  const {
    h, torsoWidth, torsoDepth, torsoHeight,
    shoulderWidth, waistWidth, hipWidth,
    armLength, armThickness,
    legLength, legThickness,
    headSize, muscleFactor, fatFactor, gender
  } = proportions;

  const skinColor = gender === 'female' ? SKIN_FEMALE : SKIN_MALE;
  const muscleBlend = new THREE.Color(skinColor).lerp(new THREE.Color(MUSCLE_COLOR), muscleFactor * 0.3);
  const bodyColor = `#${muscleBlend.getHexString()}`;

  // Slow rotation
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.3;
    }
  });

  const totalHeight = headSize * 2 + torsoHeight + legLength;
  const yOffset = -totalHeight / 2;

  return (
    <group ref={groupRef} position={[0, 0.1, 0]}>
      {/* Head */}
      <Sphere
        position={[0, yOffset + legLength + torsoHeight + headSize * 1.2, 0]}
        radius={headSize}
        color={skinColor}
      />

      {/* Neck */}
      <BodyPart
        position={[0, yOffset + legLength + torsoHeight + headSize * 0.1, 0]}
        args={[0.06, headSize * 0.4, 0.05]}
        color={skinColor}
      />

      {/* Torso (upper - shoulders) */}
      <BodyPart
        position={[0, yOffset + legLength + torsoHeight * 0.7, 0]}
        args={[shoulderWidth, torsoHeight * 0.4, torsoDepth]}
        color={bodyColor}
        radius={0.03}
      />

      {/* Torso (lower - waist) */}
      <BodyPart
        position={[0, yOffset + legLength + torsoHeight * 0.35, 0]}
        args={[waistWidth, torsoHeight * 0.35, torsoDepth * 0.9]}
        color={bodyColor}
        radius={0.03}
      />

      {/* Hips */}
      <BodyPart
        position={[0, yOffset + legLength + torsoHeight * 0.05, 0]}
        args={[hipWidth, torsoHeight * 0.2, torsoDepth * 0.95]}
        color={bodyColor}
        radius={0.03}
      />

      {/* Left Arm */}
      <BodyPart
        position={[-(shoulderWidth / 2 + armThickness), yOffset + legLength + torsoHeight * 0.55, 0]}
        args={[armThickness, armLength, armThickness * 0.9]}
        color={skinColor}
        radius={0.02}
      />

      {/* Right Arm */}
      <BodyPart
        position={[(shoulderWidth / 2 + armThickness), yOffset + legLength + torsoHeight * 0.55, 0]}
        args={[armThickness, armLength, armThickness * 0.9]}
        color={skinColor}
        radius={0.02}
      />

      {/* Left Leg */}
      <BodyPart
        position={[-(hipWidth * 0.3), yOffset + legLength * 0.5, 0]}
        args={[legThickness, legLength, legThickness * 0.9]}
        color={bodyColor}
        radius={0.02}
      />

      {/* Right Leg */}
      <BodyPart
        position={[(hipWidth * 0.3), yOffset + legLength * 0.5, 0]}
        args={[legThickness, legLength, legThickness * 0.9]}
        color={bodyColor}
        radius={0.02}
      />
    </group>
  );
}

// ─── Measurement Labels ───────────────────────────────────────────────────────

function MeasurementLabels({ measurements }) {
  if (!measurements) return null;
  const { height_cm, weight_kg, wingspan_cm, body_fat_pct } = measurements;

  return (
    <group position={[0.6, 0, 0]}>
      {height_cm && (
        <Text position={[0, 0.4, 0]} fontSize={0.04} color="#374151" anchorX="left">
          {`Altura: ${height_cm} cm`}
        </Text>
      )}
      {weight_kg && (
        <Text position={[0, 0.32, 0]} fontSize={0.04} color="#374151" anchorX="left">
          {`Peso: ${weight_kg} kg`}
        </Text>
      )}
      {wingspan_cm && (
        <Text position={[0, 0.24, 0]} fontSize={0.04} color="#374151" anchorX="left">
          {`Envergadura: ${wingspan_cm} cm`}
        </Text>
      )}
      {body_fat_pct && (
        <Text position={[0, 0.16, 0]} fontSize={0.04} color="#374151" anchorX="left">
          {`Gordura: ${body_fat_pct}%`}
        </Text>
      )}
    </group>
  );
}

// ─── Scene ────────────────────────────────────────────────────────────────────

function Scene({ measurements, gender }) {
  const proportions = useMemo(
    () => calculateProportions(measurements, gender),
    [measurements, gender]
  );

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[3, 5, 3]} intensity={0.8} castShadow />
      <directionalLight position={[-2, 3, -2]} intensity={0.3} />
      <HumanoidBody proportions={proportions} />
      <MeasurementLabels measurements={measurements} />
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={1}
        maxDistance={4}
        minPolarAngle={Math.PI * 0.2}
        maxPolarAngle={Math.PI * 0.8}
        autoRotate={false}
      />
      <Environment preset="studio" />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Export
// ═══════════════════════════════════════════════════════════════════════════════

export default function BodyProjection3D({ measurements, gender = 'male', athleteName = '' }) {
  if (!measurements || (!measurements.height_cm && !measurements.weight_kg)) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
        <div>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Projeção Corporal 3D
          </h4>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Representação estimada — não substitui exame clínico
          </p>
        </div>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
          DIGITAL TWIN
        </span>
      </div>

      {/* 3D Canvas */}
      <div className="h-[380px] w-full bg-gradient-to-b from-gray-50 to-gray-100">
        <Canvas
          camera={{ position: [0, 0, 2.2], fov: 45 }}
          dpr={[1, 2]}
          gl={{ antialias: true, alpha: true }}
        >
          <Scene measurements={measurements} gender={gender} />
        </Canvas>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t text-center" style={{ borderColor: '#e5e7eb' }}>
        <p className="text-[9px] text-gray-400">
          Arraste para rotacionar • Scroll para zoom • Baseado em medidas antropométricas reais
        </p>
      </div>
    </div>
  );
}
